import { api } from './api';

export type CourtType =
  | 'uksc'
  | 'jcpc'
  | 'ewca_civ'
  | 'ewca_crim'
  | 'ewhc_kbd'
  | 'ewhc_chd'
  | 'ewhc_fd'
  | 'ewcc'
  | 'ewfc'
  | 'cc'
  | 'mc'
  | 'ut'
  | 'ftt'
  | 'eat'
  | 'et'
  | 'other';

export interface Court {
  id: string;
  name: string;
  shortName: string;
  type: CourtType;
  city: string | null;
  address: string | null;
  postcode: string | null;
  country: string;
  website: string | null;
  isActive: boolean;
  caseCount: number;
  hearingCount: number;
  judgeCount: number;
}

export interface Judge {
  id: string;
  title: string;
  fullName: string;
  shortName: string;
  biography: string | null;
  appointedYear: number | null;
  isActive: boolean;
  court: { id: string; shortName: string; name: string } | null;
  caseCount: number;
  hearingCount: number;
}

interface ListCourtsResponse {
  data: Court[];
}

interface ListJudgesResponse {
  data: Judge[];
}

export interface ListCourtsParams {
  type?: CourtType;
  isActive?: boolean;
  search?: string;
}

export async function fetchCourts(params: ListCourtsParams = {}): Promise<Court[]> {
  const queryParams: Record<string, string> = {};
  if (params.type) queryParams.type = params.type;
  if (params.isActive !== undefined) queryParams.isActive = String(params.isActive);
  if (params.search) queryParams.search = params.search;

  const res = await api.get<ListCourtsResponse>('/courts', { params: queryParams });
  return res.data.data ?? [];
}

export async function fetchJudges(courtId?: string): Promise<Judge[]> {
  const queryParams: Record<string, string> = {};
  if (courtId) queryParams.courtId = courtId;

  const res = await api.get<ListJudgesResponse>('/courts/judges/all', { params: queryParams });
  return res.data.data ?? [];
}

export interface CreateCourtInput {
  name: string;
  shortName: string;
  type: CourtType;
  city?: string;
  address?: string;
  postcode?: string;
  country?: string;
  website?: string;
  isActive?: boolean;
}

export async function createCourt(input: CreateCourtInput): Promise<Court> {
  const res = await api.post<{ data: Court }>('/courts', input);
  return res.data.data;
}

export async function updateCourt(id: string, input: Partial<CreateCourtInput>): Promise<Court> {
  const res = await api.patch<{ data: Court }>(`/courts/${id}`, input);
  return res.data.data;
}

export async function deleteCourt(id: string): Promise<void> {
  await api.delete(`/courts/${id}`);
}

export interface CreateJudgeInput {
  courtId?: string;
  title: string;
  fullName: string;
  shortName: string;
  biography?: string;
  appointedYear?: number;
  isActive?: boolean;
}

export async function createJudge(input: CreateJudgeInput): Promise<Judge> {
  const res = await api.post<{ data: Judge }>('/courts/judges', input);
  return res.data.data;
}

export async function updateJudge(id: string, input: Partial<CreateJudgeInput>): Promise<Judge> {
  const res = await api.patch<{ data: Judge }>(`/courts/judges/${id}`, input);
  return res.data.data;
}

export async function deleteJudge(id: string): Promise<void> {
  await api.delete(`/courts/judges/${id}`);
}

// Alias for backwards compatibility
export type ApiCourt = Court;

// Helper to get display name for court type
export const COURT_TYPE_LABELS: Record<CourtType, string> = {
  uksc: 'Supreme Court',
  jcpc: 'Privy Council',
  ewca_civ: 'Court of Appeal (Civil)',
  ewca_crim: 'Court of Appeal (Criminal)',
  ewhc_kbd: "High Court (King's Bench)",
  ewhc_chd: 'High Court (Chancery)',
  ewhc_fd: 'High Court (Family)',
  ewcc: 'County Court',
  ewfc: 'Family Court',
  cc: 'Crown Court',
  mc: "Magistrates' Court",
  ut: 'Upper Tribunal',
  ftt: 'First-tier Tribunal',
  eat: 'Employment Appeal Tribunal',
  et: 'Employment Tribunal',
  other: 'Other',
};
